/**
 * @file StandingOrderModal.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-12-10
 * @modified 2026-10-30
 * @description Modal for adding/editing standing orders
 *
 *              A standing order accepts several products — the same delivery
 *              bought from one of a few suppliers, each with its own item code.
 *              One row per product: the first is the main one (used when an
 *              order is raised from the standing order), the rest are
 *              alternatives, and "last had" counts a delivery of any of them.
 *
 *              1.5.0 (2026-10-15, Cam): an archived product. The picker never
 *              offers one the standing order does not already name (the save
 *              refuses it too, 409 ARCHIVED_PRODUCT_CHOSEN); one it names is
 *              shown with "archived — choose the replacement", and an order
 *              can't be made from the standing order until the main one is
 *              changed. Opened from that prompt (`focusProduct`), the main
 *              product's picker is highlighted and focused. Saving with it
 *              unchanged is allowed — the answer's `warnings` say it is still
 *              archived (handed to onSave).
 *
 *              1.6.0 (2026-10-30, Cam — rep order requests, 29 Oct): opened
 *              from Sales Orders' "Rep requests". `prefill` fills a NEW
 *              standing order from a rep's request (customer, product with its
 *              additives, quantity, schedule, notes); `repRequest` shows what
 *              the rep asked above the form and is sent with the save
 *              (`rep_request_id`), which completes the request in the same
 *              transaction (refused, nothing saved, if it was done meanwhile).
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import './StandingOrders.css';
import SearchableDropdown from './SearchableDropdown';
import Input from './Input';
import Dropdown from './Dropdown';
import AlertDialog from './AlertDialog';
import AuditHistoryModal from './AuditHistoryModal';
import AdditiveMultiPicker from './AdditiveMultiPicker';
import { useSlideOutButton } from './SlideOutButton';
import { formatDecimalInput } from '../utils/numberFormatters';
import FindInSearchButton from './FindInSearchButton';
import { amendChanges, lineText, scheduleText, requestRef, KIND_WORDS } from '../utils/repOrderRequests';
import './RepOrderRequests.css';

const DAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' }
];

const INTERVAL_TYPE_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' }
];

function StandingOrderModal({ standingOrder, mode = 'edit', focusProduct = false, onClose, onSave, prefill = null, repRequest = null }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [closing, setClosing] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [showHistory, setShowHistory] = useState(false);

  // Product rows, main first — each with the additives it carries. Kept apart
  // from formData because it is a list.
  const [rows, setRows] = useState([{ item_id: '', additives: [] }]);
  const itemIds = rows.map(r => r.item_id);

  // The products the standing order named when opened: an archived one may
  // stay (and be shown), but no other archived product is offered.
  const originalIds = useMemo(() => new Set([
    ...(standingOrder?.products || []).map(p => String(p.item_id)),
    ...(standingOrder?.item_id ? [String(standingOrder.item_id)] : [])
  ]), [standingOrder]);
  const archivedOnOrder = useMemo(() => new Set((standingOrder?.products || [])
    .filter(p => p.is_archived).map(p => String(p.item_id))), [standingOrder]);
  const mainRowRef = useRef(null);

  // Whose additive list a product uses, by the first character of its code.
  const [additiveSuppliers, setAdditiveSuppliers] = useState({});

  // Slideout buttons — re-measured as product rows come and go.
  useSlideOutButton([rows.length]);

  const [formData, setFormData] = useState({
    account_id: '',
    customer_code: '',
    product_code: '',
    quantity: '',
    schedule_type: 'weekly',
    day_of_week: '1',
    interval_value: '',
    interval_type: 'days',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    loadCustomers();
    loadProducts();
    loadAdditiveSuppliers();

    // Prevent background scrolling
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) {
      contentWrapper.classList.add('modal-open');
    }

    // A new standing order from a rep's request (Sales Orders' "Rep requests").
    if (!standingOrder && prefill) {
      const listed = (prefill.products || []).map(p => ({
        item_id: String(p.item_id),
        additives: Array.isArray(p.additives) ? p.additives : [],
      }));
      setRows(listed.length ? listed : [{ item_id: '', additives: [] }]);
      setFormData(prev => ({
        ...prev,
        account_id: prefill.account_id ? String(prefill.account_id) : '',
        quantity: prefill.quantity === '' || prefill.quantity === null || prefill.quantity === undefined ? '' : formatDecimalInput(prefill.quantity),
        schedule_type: prefill.schedule_type || 'weekly',
        day_of_week: prefill.day_of_week ? String(prefill.day_of_week) : '1',
        interval_value: prefill.interval_value || '',
        interval_type: prefill.interval_type || 'days',
        notes: prefill.notes || ''
      }));
    }

    // Pre-populate form if editing
    if (standingOrder) {
      const listed = (standingOrder.products || []).map(p => ({
        item_id: String(p.item_id),
        additives: Array.isArray(p.additives) ? p.additives : [],
      }));
      setRows(listed.length
        ? listed
        : [{ item_id: standingOrder.item_id ? standingOrder.item_id.toString() : '', additives: [] }]);
      setFormData({
        account_id: standingOrder.account_id ? standingOrder.account_id.toString() : '',
        customer_code: standingOrder.customer_code || '',
        product_code: standingOrder.product_code || '',
        quantity: formatDecimalInput(standingOrder.quantity),
        schedule_type: standingOrder.schedule_type || 'weekly',
        day_of_week: standingOrder.day_of_week ? standingOrder.day_of_week.toString() : '1',
        interval_value: standingOrder.interval_value || '',
        interval_type: standingOrder.interval_type || 'days',
        status: standingOrder.status || (standingOrder.is_active === false ? 'inactive' : 'active'),
        notes: standingOrder.notes || ''
      });
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standingOrder]);

  // Opened from "Product archived — Edit standing order": take them to the
  // main product's picker.
  useEffect(() => {
    if (!focusProduct) return undefined;
    const t = setTimeout(() => {
      const row = mainRowRef.current;
      if (!row) return;
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const target = row.querySelector('.so-product-picker input, .so-product-picker [tabindex], .so-product-picker button');
      if (target) target.focus();
    }, 350);
    return () => clearTimeout(t);
  }, [focusProduct]);

  // Is this item archived here? From the picker's list, else the standing order's own.
  const isArchivedItem = (itemId) => {
    if (!itemId) return false;
    const item = products.find(p => String(p.id) === String(itemId));
    return item ? !!item.is_archived : archivedOnOrder.has(String(itemId));
  };

  const loadAdditiveSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/standing-orders/additive-suppliers`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setAdditiveSuppliers(data.suppliers || {});
    } catch (error) {
      console.error('Error loading additive suppliers:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/standing-orders/customers`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/standing-orders/products`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
      onClose();
    }, 300);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-populate customer_code when customer is selected
    if (field === 'account_id' && value) {
      const customer = customers.find(c => c.id.toString() === value);
      if (customer) {
        setFormData(prev => ({ ...prev, account_id: value, customer_code: customer.account_no || '' }));
      }
    }

  };

  // A new product starts with no additives: the old ones belonged to the old
  // product and very likely to another supplier's list.
  const setProductAt = (index, value) => {
    setRows(prev => prev.map((r, i) => (i === index
      ? { item_id: value || '', additives: r.item_id === (value || '') ? r.additives : [] }
      : r)));
  };

  const setAdditivesAt = (index, additives) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, additives } : r)));
  };

  const addProductRow = () => setRows(prev => [...prev, { item_id: '', additives: [] }]);

  // Removing the last row leaves one empty row rather than none, so there is
  // always somewhere to pick a product.
  const removeProductAt = (index) => {
    setRows(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ item_id: '', additives: [] }];
    });
  };

  /**
   * What additives a product may carry, by the rules:
   *   - only a compound (sales 201/207 or purchase 311/317) carries any;
   *   - a code starting 1 uses Mole Valley Feed Solutions' list, 2 NWF's;
   *   - any other compound takes free text.
   * The server applies the same rules on save.
   */
  const additiveModeFor = (itemId) => {
    const item = products.find(p => String(p.id) === String(itemId));
    if (!item) return { kind: 'none', reason: 'Choose a product first' };
    const sales = String(item.sales_account_code || '').toUpperCase();
    const purchase = String(item.purchase_account_code || '').toUpperCase();
    const compound = ['201', '207'].includes(sales) || ['311', '317'].includes(purchase);
    if (!compound) return { kind: 'none', reason: 'N/A' };
    const prefix = String(item.item_code || '').trim().charAt(0);
    if (prefix === '1' || prefix === '2') {
      const supplier = additiveSuppliers[prefix];
      return supplier
        ? { kind: 'list', supplierId: supplier.id, supplierName: supplier.name }
        : { kind: 'none', reason: 'Loading additive list…' };
    }
    return { kind: 'free' };
  };

  // A product already on another row is not offered again, nor an archived
  // one the standing order does not already name (the save refuses it too).
  const productOptionsFor = (index) => [
    { value: '', label: 'Select product...' },
    ...products
      .filter(p => !itemIds.some((id, i) => i !== index && id === p.id.toString()))
      .filter(p => !p.is_archived || originalIds.has(p.id.toString()))
      .map(p => ({
        value: p.id.toString(),
        badge: p.item_code || ' ',
        label: p.item_name,
        // Hidden behind the picker's own toggle unless this order already
        // names it. Seasonal blends are archived the moment their season
        // ends, and they outnumber the live ones.
        archived: !!p.is_archived,
        searchText: `${p.item_code || ''} ${p.item_name}`
      }))
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setAlertDialog({ isOpen: true, message: 'Please enter a valid quantity', type: 'error' });
      return;
    }

    if (formData.schedule_type === 'interval' && (!formData.interval_value || parseInt(formData.interval_value) <= 0)) {
      setAlertDialog({ isOpen: true, message: 'Please enter a valid interval value', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const chosenRows = rows.filter(r => r.item_id);
      const chosen = chosenRows.map(r => parseInt(r.item_id, 10));
      const payload = {
        account_id: formData.account_id ? parseInt(formData.account_id) : null,
        // Main product first; the server keeps item_id and product_code equal to it.
        item_ids: chosen,
        // Each product with its additives; the server re-checks the rules.
        products: chosenRows.map(r => ({
          item_id: parseInt(r.item_id, 10),
          additives: additiveModeFor(r.item_id).kind === 'none' ? [] : r.additives,
        })),
        customer_code: formData.customer_code || null,
        // Kept only for an imported order whose code matched no product.
        product_code: chosen.length ? null : (formData.product_code || null),
        quantity: parseFloat(formData.quantity),
        schedule_type: formData.schedule_type,
        day_of_week: formData.schedule_type === 'weekly' ? parseInt(formData.day_of_week) : null,
        interval_value: formData.schedule_type === 'interval' ? parseInt(formData.interval_value) : null,
        interval_type: formData.schedule_type === 'interval' ? formData.interval_type : null,
        status: formData.status,
        notes: formData.notes || null,
        // Made from (or changed for) a rep's request: completed with this save.
        ...(repRequest?.id ? { rep_request_id: repRequest.id } : {})
      };

      const url = standingOrder
        ? `${API_URL}/api/standing-orders/${standingOrder.id}`
        : `${API_URL}/api/standing-orders`;

      const response = await fetch(url, {
        method: standingOrder ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        // `warnings`: saved with its main product still archived.
        onSave(data);
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to save standing order', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving standing order:', error);
      setAlertDialog({ isOpen: true, message: 'Error saving standing order', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content modal-lg standing-order-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ paddingBottom: 'var(--spacing-lg)' }}>
          <h3 style={{ marginBottom: '0' }}>
            {standingOrder ? 'Edit Standing Order' : 'New Standing Order'}
            {repRequest?.id ? ` — from ${requestRef(repRequest.id)}` : ''}
          </h3>
          <div className="header-actions">
            {standingOrder?.id && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowHistory(true)}
                title="View change history"
              >
                <span className="material-symbols-rounded">history</span>
              </button>
            )}
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
              {repRequest && (
                <div className="ror-banner" role="note">
                  <span className="ror-banner-title">
                    {KIND_WORDS[repRequest.kind]} from {repRequest.rep_name || 'a rep'} ({requestRef(repRequest.id)})
                    {' '}— saving this completes it
                  </span>
                  {repRequest.kind === 'amend_standing_order' ? (
                    <ul>{(amendChanges(repRequest).length ? amendChanges(repRequest) : ['Nothing differs — see the notes']).map((c, i) => <li key={i}>{c}</li>)}</ul>
                  ) : (
                    <ul>
                      {(repRequest.lines || []).map((l, i) => <li key={i}>{lineText(l)}</li>)}
                      {repRequest.schedule && <li>{scheduleText(repRequest.schedule)}</li>}
                    </ul>
                  )}
                  {repRequest.notes && <span>“{repRequest.notes}”</span>}
                  {(repRequest.lines || []).some(l => !l.item_id && l.text) && (
                    <span>The rep typed a product ({(repRequest.lines || []).filter(l => !l.item_id).map(l => l.text).join(', ')}) — choose it below.</span>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>Customer</label>
                <SearchableDropdown
                  value={formData.account_id}
                  onChange={(value) => handleChange('account_id', value)}
                  options={[
                    { value: '', label: 'Select customer...' },
                    ...customers.map(c => ({
                      value: c.id.toString(),
                      badge: c.account_no || ' ',
                      label: c.company_name,
                      searchText: `${c.account_no || ''} ${c.company_name}`
                    }))
                  ]}
                  placeholder="Select customer..."
                />
                {!formData.account_id && formData.customer_code && (
                  <span className="field-note">Original code: {formData.customer_code}</span>
                )}
              </div>

              <div className="form-group">
                <label>Products</label>
                <div className="so-products">
                  {rows.map(({ item_id: id, additives }, index) => {
                    const mode = additiveModeFor(id);
                    const archived = isArchivedItem(id);
                    return (
                    <div
                      className={`so-product-row${archived ? ' is-archived' : ''}${index === 0 && focusProduct ? ' is-focused' : ''}`}
                      key={index}
                      ref={index === 0 ? mainRowRef : undefined}
                    >
                      <span
                        className={`so-product-rank ${index === 0 ? 'is-main' : ''}`}
                        title={index === 0
                          ? 'The product an order raised from this standing order starts with'
                          : 'An alternative — the same delivery from another supplier'}
                      >
                        {index === 0 ? 'Main' : 'or'}
                      </span>
                      <div className="so-product-picker">
                        <SearchableDropdown
                          value={id}
                          onChange={(value) => setProductAt(index, value)}
                          options={productOptionsFor(index)}
                          placeholder="Select product..."
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => removeProductAt(index)}
                        disabled={rows.length === 1 && !id}
                        title="Remove this product"
                      >
                        <span className="material-symbols-rounded">delete</span>
                      </button>
                      {archived && (
                        <div className="so-archived-warning">
                          <span className="material-symbols-rounded">warning</span>
                          {index === 0
                            ? 'Archived — choose the replacement. No order can be made from this standing order until it is changed; find out from the customer (or their rep) what replaces it.'
                            : 'Archived — not offered when an order is made. Remove it, or choose the product that replaces it.'}
                        </div>
                      )}
                      {/* This product's additives, from its supplier's list —
                          or free text for a compound with no list. */}
                      <div className="so-product-additives">
                        {mode.kind === 'list' && (
                          <AdditiveMultiPicker
                            supplierAccountId={mode.supplierId}
                            value={additives}
                            onChange={(next) => setAdditivesAt(index, next)}
                            placeholder={additives.length ? undefined : `${mode.supplierName} additives…`}
                          />
                        )}
                        {mode.kind === 'free' && (
                          <Input
                            type="text"
                            value={additives[0]?.additive_name || ''}
                            onChange={(e) => setAdditivesAt(index, e.target.value
                              ? [{ additive_id: null, additive_code: null, additive_name: e.target.value, is_free_text: true }]
                              : [])}
                            placeholder="Additive (free text — no supplier list for this code)"
                          />
                        )}
                        {mode.kind === 'none' && (
                          <AdditiveMultiPicker supplierAccountId={null} value={[]} onChange={() => {}}
                                               disabled placeholder={mode.reason} />
                        )}
                      </div>
                    </div>
                    );
                  })}
                  <div className="so-add-product">
                    <button type="button" className="btn-slideout" onClick={addProductRow}>
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">add</span>
                      </div>
                      <span className="btn-label">Add Product</span>
                    </button>
                  </div>
                </div>
                {!itemIds.some(Boolean) && formData.product_code && (
                  <span className="field-note">Original code: {formData.product_code}</span>
                )}
              </div>

            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr ' }} >
              <div className="form-group">
                <label>Quantity</label>
                <Input
                  type="number"
                  step="0.001"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                  // Tidied on leaving the field, never while typing. The save
                  // parses it with parseFloat.
                  onBlur={() => handleChange('quantity', formatDecimalInput(formData.quantity))}
                  placeholder="Enter quantity"
                />
              </div>

              <div className="form-group">
                <label>Schedule Type</label>
                <Dropdown
                  value={formData.schedule_type}
                  onChange={(value) => handleChange('schedule_type', value)}
                  options={[
                    { value: 'weekly', label: 'Weekly (Mon-Fri)' },
                    { value: 'interval', label: 'Custom Interval' }
                  ]}
                />
              </div>

              {formData.schedule_type === 'weekly' && (
                <div>
                  <div className="form-group" >
                    <label>Day of Week</label>
                    <Dropdown
                      value={formData.day_of_week}
                      onChange={(value) => handleChange('day_of_week', value)}
                      options={DAY_OPTIONS}
                    />
                  </div>
                  <div></div>
                </div>
              )}

              {formData.schedule_type === 'interval' && (
                <div className="form-group">
                  <label>Every</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.interval_value}
                    onChange={(e) => handleChange('interval_value', e.target.value)}
                    placeholder="e.g. 14"
                  />
                </div>
              )}
              
              {formData.schedule_type === 'interval' && (
                <div className="form-group">
                  <label>Interval Type</label>
                  <Dropdown
                    value={formData.interval_type}
                    onChange={(value) => handleChange('interval_type', value)}
                    options={INTERVAL_TYPE_OPTIONS}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Status</label>
                <Dropdown
                  value={formData.status}
                  onChange={(value) => handleChange('status', value)}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'held', label: 'Held' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                />
              </div>
            </div>

            <div className="form-grid" >
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Optional notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-primary)',
                    fontFamily: 'inherit',
                    fontSize: 'var(--font-sm)',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <FindInSearchButton type="standing_order" record={standingOrder} onClose={onClose} />
            <button type="submit" className="btn-slideout btn-slideout-primary" disabled={saving}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>
                  {saving ? 'sync' : 'save'}
                </span>
              </div>
              <span className="btn-label">{saving ? 'Saving...' : (standingOrder ? 'Update' : 'Create')}</span>
            </button>
            <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">close</span>
              </div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
        </form>

        <AlertDialog
          isOpen={alertDialog.isOpen}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        />

        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="standing_order"
          entityId={standingOrder?.id}
          entityIdentifier={standingOrder?.reference}
        />
      </div>
    </div>,
    document.body
  );
}

export default StandingOrderModal;
